import { Icon as BlueIcon } from '@blueprintjs/core';
import { useFocusWithin, useSetState } from '@mantine/hooks';

import { useEffect, useRef } from 'react';
import {
  Buttons,
  Close,
  Container,
  Div,
  Line,
  Search,
  SearchContainer,
  ShowOptions,
  StyledOption,
} from './SearchBarExpansiveCustomField.styles';

export interface ContentAndIdAndManufacturer {
  content: string;
  id: string;
  manufacturerName?: string;
}
interface SearchBarProps {
  options: string[];
  selectedValue: string | null;
  placeholder?: string;
  isDisabled?: boolean;
  onCloseIconClick?: () => void;
  onSelect?: (input: string) => void;
  isActuallyDisabled?: boolean;
}
const SearchBarExpansiveCustomField = ({
  selectedValue,
  isDisabled,
  placeholder,
  onCloseIconClick,
  options,
  onSelect,
  isActuallyDisabled,
}: SearchBarProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // iNPUT VALUES
  const [searchState, setSearchState] = useSetState({
    showErase: Boolean(selectedValue),
    showOptions: false,
    searchText: '',
  });
  const { ref, focused } = useFocusWithin<HTMLDivElement>({
    onBlur() {
      // ref: https://stackoverflow.com/questions/17769005/onclick-and-onblur-ordering-issue/73922116#73922116
      if (dropdownRef.current && !dropdownRef.current.matches(':hover')) {
        setSearchState({
          showOptions: false,
        });
      }
    },
    onFocus() {
      // ref: https://github.com/mantinedev/mantine/blob/master/src/mantine-hooks/src/use-focus-within/use-focus-within.ts#L28
      if (!selectedValue) {
        setSearchState({ showOptions: true });
      }
    },
  });

  const { showErase, showOptions, searchText } = searchState;
  useEffect(() => {
    // For when we update single inputs
    const _showErase = Boolean(selectedValue);
    if (_showErase) {
      setSearchState({ showErase: _showErase });
    }
    setSearchState({ showErase: _showErase });
  }, [selectedValue]);
  const shouldShow = selectedValue !== null ? false : focused || showOptions;
  const $optionsToCalc = shouldShow ? options : [];
  const $searchText = selectedValue ?? searchText ?? '';
  const lowerSearch = $searchText.toLowerCase();
  const $options = $optionsToCalc.filter(value =>
    value.toLowerCase().includes(lowerSearch),
  );

  const shouldShowErase = showErase && selectedValue;
  let className: string | undefined;
  if (shouldShowErase) {
    className = 'close';
  }
  return (
    <Div ref={ref}>
      <SearchContainer>
        <div>
          <Search
            autoComplete="off"
            readOnly={showErase}
            disabled={isDisabled || isActuallyDisabled}
            data-disabled={isActuallyDisabled}
            placeholder={placeholder}
            value={$searchText}
            onChange={event => {
              setSearchState({ searchText: event.target.value });
            }}
            haveResults={$options ? $options.length > 0 : false}
            className={className}
          />
        </div>

        {shouldShowErase && (
          <Close
            onClick={() => {
              if (onCloseIconClick) onCloseIconClick();
              setSearchState({ searchText: '' });
            }}
            hidden={isActuallyDisabled}
          >
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        )}
      </SearchContainer>
      {$options && $options.length > 0 && <Line hidden={isActuallyDisabled} />}
      {!isActuallyDisabled && $options && $options.length > 0 && (
        <ShowOptions ref={dropdownRef} haveResults={$options.length > 0}>
          <Container className="bscroll" haveResults={$options.length > 0}>
            {$options.map(item => (
              <StyledOption
                onClick={() => {
                  if (onSelect) onSelect(item);
                  setSearchState({
                    showOptions: false,
                  });
                }}
              >
                {item}
              </StyledOption>
            ))}
          </Container>
          <Buttons />
        </ShowOptions>
      )}
    </Div>
  );
};

export { SearchBarExpansiveCustomField };
